import React, { useState } from 'react';
import { useLocale } from '@platform/core';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../contexts/firebase';
import { usePlanning } from '../contexts/PlanningContext';
import { MagicGrid } from '../components/MagicGrid';

interface NewCompanyData {
  CompanyName: string;
  Owner: string;
  ContactPerson: string;
  ContactEmail: string;
  ContactPhone: string;
  Address: string;
  Website: string;
}

const emptyCompany: NewCompanyData = {
  CompanyName: '',
  Owner: '',
  ContactPerson: '',
  ContactEmail: '',
  ContactPhone: '',
  Address: '',
  Website: '',
};

export default function CompaniesPage() {
  const { t } = useLocale();
  const { companies, currentCompany, selectCompany } = usePlanning();
  const [manageMode, setManageMode] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [newCompany, setNewCompany] = useState<NewCompanyData>({ ...emptyCompany });

  const handleCreateCompany = async () => {
    if (!newCompany.CompanyName) return;
    const docRef = await addDoc(collection(db, 'companies'), {
      ...newCompany,
      Active: true,
      Users: [newCompany.Owner],
      CreatedDate: serverTimestamp(),
      UpdateDate: serverTimestamp(),
    });
    selectCompany(docRef.id);
    setNewCompany({ ...emptyCompany });
    setAddMode(false);
  };

  return (
    <div className="companies-page">
      <div className="page-header">
        <h1>🏢 {t('planning.companies') || 'Companies'}</h1>
      </div>

      {/* Company Cards */}
      <div className="company-cards">
        {companies.map((company) => (
          <div
            key={company.id}
            className={`company-card ${currentCompany?.id === company.id ? 'selected' : ''}`}
            onClick={() => selectCompany(company.id)}
          >
            <div className="company-card-header">
              {company.CompanyName}
            </div>
            <div className="company-card-body">
              {(company as any).Owner && (
                <div><strong>{t('common.owner') || 'Owner'}:</strong> {(company as any).Owner}</div>
              )}
              {(company as any).ContactPerson && (
                <div><strong>{t('common.contactPerson') || 'Contact'}:</strong> {(company as any).ContactPerson}</div>
              )}
              {(company as any).ContactEmail && (
                <div><strong>{t('common.email') || 'Email'}:</strong> {(company as any).ContactEmail}</div>
              )}
              {(company as any).ContactPhone && (
                <div><strong>{t('common.phone') || 'Phone'}:</strong> {(company as any).ContactPhone}</div>
              )}
              {(company as any).Active !== undefined && (
                <div><strong>{t('common.active') || 'Active'}:</strong> {(company as any).Active ? '✅' : '❌'}</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Manage Companies Toggle */}
      <div className="manage-section">
        <button
          className={`mg-btn ${manageMode ? 'mg-btn-primary' : 'mg-btn-outline'}`}
          onClick={() => setManageMode(!manageMode)}
        >
          ⚙️ {t('planning.manageCompanies') || 'Manage Companies'}
        </button>

        {manageMode && (
          <div className="manage-content">
            <button
              className={`mg-btn ${addMode ? 'mg-btn-primary' : 'mg-btn-outline'}`}
              onClick={() => setAddMode(!addMode)}
              style={{ marginBottom: '1rem' }}
            >
              ➕ {t('common.addNew') || 'Add New'}
            </button>

            {addMode && (
              <div className="add-company-form">
                <div className="form-grid">
                  {Object.entries(newCompany).map(([key, value]) => (
                    <div key={key} className="form-field">
                      <label>{t(`common.${key.toLowerCase()}`) || key}</label>
                      <input
                        className="mg-cell-input"
                        value={value}
                        onChange={(e) =>
                          setNewCompany((d) => ({ ...d, [key]: e.target.value }))
                        }
                        placeholder={key}
                      />
                    </div>
                  ))}
                </div>
                <div className="form-actions">
                  <button className="mg-btn mg-btn-primary" onClick={handleCreateCompany}>
                    {t('common.add') || 'Add'}
                  </button>
                  <button
                    className="mg-btn mg-btn-outline"
                    onClick={() => {
                      setAddMode(false);
                      setNewCompany({ ...emptyCompany });
                    }}
                  >
                    {t('common.cancel') || 'Cancel'}
                  </button>
                </div>
              </div>
            )}

            <MagicGrid
              firestorePath="companies"
              editable
              addable
              deletable
              options={{ hideCols: ['updatedate', 'createddate'], roCols: ['id', 'companyid'] }}
            />
          </div>
        )}
      </div>

      <style>{`
        .companies-page {
          max-width: 1200px;
          margin: 0 auto;
        }

        .page-header {
          margin-bottom: 1.5rem;
        }

        .page-header h1 {
          margin: 0;
          font-size: 1.5rem;
        }

        .company-cards {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .company-card {
          border: 2px solid var(--border-color, #e0e0e0);
          border-radius: var(--radius-lg, 12px);
          overflow: hidden;
          cursor: pointer;
          transition: all 0.15s;
        }

        .company-card:hover {
          border-color: var(--primary-color, #1976d2);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }

        .company-card.selected {
          border-color: var(--primary-color, #1976d2);
        }

        .company-card-header {
          padding: 0.75rem 1rem;
          font-weight: 600;
          color: white;
          background: var(--text-primary, #333);
        }

        .company-card.selected .company-card-header {
          background: var(--primary-color, #1976d2);
        }

        .company-card-body {
          padding: 1rem;
          display: flex;
          flex-direction: column;
          gap: 0.375rem;
          font-size: 0.875rem;
        }

        .manage-section {
          margin-top: 1rem;
        }

        .manage-content {
          margin-top: 1rem;
        }

        .add-company-form {
          background: #f0f7ff;
          border-radius: var(--radius-lg, 12px);
          padding: 1.5rem;
          margin-bottom: 1rem;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 1rem;
        }

        .form-field {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .form-field label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--text-secondary, #757575);
        }

        .form-actions {
          display: flex;
          gap: 0.5rem;
          margin-top: 1rem;
        }

        .mg-btn {
          padding: 0.375rem 0.75rem;
          border-radius: var(--radius-md, 8px);
          font-size: 0.8125rem;
          cursor: pointer;
          border: 1px solid var(--border-color, #e0e0e0);
          background: var(--surface-color, #fff);
          transition: all 0.15s;
        }

        .mg-btn:hover { background: var(--background-color, #f5f5f5); }

        .mg-btn-primary {
          background: var(--primary-color, #1976d2);
          color: white;
          border-color: var(--primary-color, #1976d2);
        }

        .mg-btn-outline { background: transparent; }

        .mg-cell-input {
          width: 100%;
          padding: 0.375rem 0.5rem;
          border: 1px solid var(--border-color, #e0e0e0);
          border-radius: 4px;
          font-size: 0.875rem;
        }

        @media (max-width: 768px) {
          .companies-page { max-width: 100%; }
          .company-cards { grid-template-columns: 1fr; }
          .form-grid { grid-template-columns: 1fr; }
          .add-company-form { padding: 0.75rem; }
          .mg-btn { min-height: 44px; }
          .mg-cell-input { min-height: 44px; }
        }
      `}</style>
    </div>
  );
}
